import { useMemo, useRef, useState } from "react";
import moment, { type Moment } from "moment";
import { useIntl } from "react-intl";

import { Stack } from "@certa/catalyst/layouts/Stack";
import {
  DatePickerFormField,
  SelectFormField,
  TextInputFormField,
  TextAreaFormField,
  type FieldValues,
  useFormContext
} from "@certa/catalyst/components/Form";

import {
  useGetDelegatees,
  type Delegatee,
  type DelegationRequest
} from "@certa/queries/queries/massDelegation/massDelegation.query";

import { useDebouncedValue } from "../../../hooks/useDebounce";
import { useAuth } from "@certa/auth/AuthenticationProvider";

type TransformedDelegationRequest = Omit<
  DelegationRequest,
  "user" | "creator"
> & {
  delegate: string;
};

type DelegateTasksFormFieldsProps = {
  onUpdateDelegate: (value?: Delegatee["results"][number]) => void;
  selectedDelegationRequest?: TransformedDelegationRequest;
  isDisabled?: boolean;
};

export const DelegateTasksFormFields = (
  props: DelegateTasksFormFieldsProps
) => {
  const {
    onUpdateDelegate,
    selectedDelegationRequest,
    isDisabled = false
  } = props;

  const { user } = useAuth();

  const intl = useIntl();

  const accumulatedOptionsRef = useRef<{ label: string; value: string }[]>([]);

  const [searchDelegateValue, setSearchDelegateValue] = useState("");

  const debouncedSearchDelegateValue = useDebouncedValue(
    searchDelegateValue,
    500
  );

  const {
    data: delegatees,
    isLoading: isLoadingDelegatees,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage: isInfiniteScrollLoading
  } = useGetDelegatees({
    search: debouncedSearchDelegateValue
  });

  const delegateUserOptions = useMemo(() => {
    return delegatees?.pages.reduce(
      (acc: Delegatee["results"], page: Delegatee) => {
        return [...acc, ...page.results];
      },
      []
    );
  }, [delegatees]);

  const options = useMemo(() => {
    if (delegateUserOptions?.length) {
      const transformedOptions = delegateUserOptions.map((delegate: any) => ({
        label: delegate.email,
        value: delegate.email
      }));
      // Only append new options that aren't already in the accumulated list
      const uniqueNewOptions = transformedOptions.filter(
        newOpt =>
          !accumulatedOptionsRef.current.some(
            existingOpt => existingOpt.value === newOpt.value
          )
      );

      accumulatedOptionsRef.current = [
        ...accumulatedOptionsRef.current,
        ...uniqueNewOptions
      ];
    }

    return accumulatedOptionsRef.current;
  }, [delegateUserOptions]);

  const onScrollToBottom = () => {
    if (hasNextPage && !isInfiniteScrollLoading) {
      fetchNextPage();
    }
  };

  const onSearchDelegate = (value: string) => {
    setSearchDelegateValue(encodeURIComponent(value));
    accumulatedOptionsRef.current = [];
  };

  const isStartDateDisabled =
    (selectedDelegationRequest?.startDate
      ? moment(selectedDelegationRequest.startDate).isBefore(moment())
      : false) || isDisabled;

  const onSelectDelegate = (value: string) => {
    const delegate = delegateUserOptions?.find(
      delegate => delegate.email === value
    );
    onUpdateDelegate(delegate);
  };

  const delegationNameLabel = intl.formatMessage({
    id: "delegations.delegationName",
    defaultMessage: "Delegation Name"
  });

  const maxLengthValidationMessage = intl.formatMessage({
    id: "fieldErrors.err_field_max_length",
    defaultMessage: "Ensure this field has no more than 255 characters"
  });

  const delegationNamePlaceholder = intl.formatMessage({
    id: "delegations.delegationNamePlaceholder",
    defaultMessage: "Enter a name"
  });

  const selectDelegateLabel = intl.formatMessage({
    id: "delegations.selectDelegate",
    defaultMessage: "Select Delegate"
  });

  const selectDelegatePlaceholder = intl.formatMessage({
    id: "delegations.selectDelegatePlaceholder",
    defaultMessage: "Select user"
  });

  const startDateLabel = intl.formatMessage({
    id: "delegations.startDate",
    defaultMessage: "Start Date"
  });

  const endDateLabel = intl.formatMessage({
    id: "commonTextInstances.endDate",
    defaultMessage: "End Date"
  });

  const reasonNotesLabel = intl.formatMessage({
    id: "delegations.reasonNotes",
    defaultMessage: "Reason/Notes"
  });

  const reasonNotesPlaceholder = intl.formatMessage({
    id: "delegations.reasonNotesPlaceholder",
    defaultMessage:
      "Enter the reason for your delegation or any important details"
  });

  const requiredFieldText = intl.formatMessage({
    id: "fieldErrors.err_field_mandatory",
    defaultMessage: "This field is required"
  });

  const startDateValidationMessage = intl.formatMessage({
    id: "delegations.startDateValidationMessage",
    defaultMessage: "Start date cannot be after end date"
  });

  const endDateValidationMessage = intl.formatMessage({
    id: "delegations.endDateValidationMessage",
    defaultMessage: "End date cannot be before start date"
  });

  const selectDelegateValidationMessage = intl.formatMessage({
    id: "delegations.selectDelegateValidationMessage",
    defaultMessage: "You cannot delegate to yourself"
  });

  const { clearErrors } = useFormContext();

  const startDateValidationRules = {
    required: requiredFieldText,
    validate: (value: Moment, formData: FieldValues) => {
      const formValues = formData as TransformedDelegationRequest;
      clearErrors("endDate");

      if (
        value &&
        formValues?.endDate &&
        moment(value).isAfter(formValues?.endDate)
      ) {
        return startDateValidationMessage;
      }
      return undefined;
    }
  };

  const endDateValidationRules = {
    required: requiredFieldText,
    validate: (value: Moment, formData: FieldValues) => {
      const formValues = formData as TransformedDelegationRequest;
      clearErrors("startDate");

      if (
        value &&
        formValues?.startDate &&
        moment(value).isBefore(formValues?.startDate)
      ) {
        return endDateValidationMessage;
      }
      return true;
    }
  };

  const selectDelegateValidationRules = {
    required: requiredFieldText,
    validate: (value: string) => {
      if (value && value === user?.email) {
        return selectDelegateValidationMessage;
      }
      return true;
    }
  };

  const delegationNameValidationRules = {
    required: requiredFieldText,
    maxLength: {
      value: 255,
      message: maxLengthValidationMessage
    }
  };

  return (
    <>
      <TextInputFormField
        width="100%"
        label={delegationNameLabel}
        name="delegationName"
        placeholder={delegationNamePlaceholder}
        required={true}
        rules={delegationNameValidationRules}
        disabled={isDisabled}
      />
      <SelectFormField
        width="100%"
        label={selectDelegateLabel}
        placeholder={selectDelegatePlaceholder}
        name="selectDelegate"
        options={options}
        required={true}
        rules={selectDelegateValidationRules}
        handleScrolledToBottom={onScrollToBottom}
        isLoading={isLoadingDelegatees}
        onSearch={onSearchDelegate}
        onChange={onSelectDelegate}
        disabled={isDisabled}
      />
      <Stack direction="horizontal" gap="16" width="100%">
        <DatePickerFormField
          label={startDateLabel}
          name="startDate"
          inputWidth="100%"
          aria-label={startDateLabel}
          placeholder="YYYY-MM-DD"
          disabled={isStartDateDisabled}
          required={true}
          minDate={moment().add(1, "day")}
          rules={startDateValidationRules}
        />
        <DatePickerFormField
          label={endDateLabel}
          name="endDate"
          inputWidth="100%"
          placeholder="YYYY-MM-DD"
          required={true}
          minDate={moment().add(1, "day")}
          rules={endDateValidationRules}
          disabled={isDisabled}
        />
      </Stack>
      <TextAreaFormField
        label={reasonNotesLabel}
        name="reasonNotes"
        placeholder={reasonNotesPlaceholder}
        rows={3}
        required={true}
        rules={{
          required: requiredFieldText
        }}
        disabled={isDisabled}
      />
    </>
  );
};
